import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import { type TaggerEntityType } from "../../../zodios/apiDefinitions/api/taggers";
import { successToast, errorToast } from "../../../utils/toasts";

interface TaggerProgram {
  id: number;
  name: string;
  status: string;
  start_date: string | null;
  end_date: string | null;
  spec: Record<string, unknown> | null;
  ui_state: Record<string, unknown> | null;
  run_cnt: number;
  last_run: string | null;
  last_action: string | null;
  created_on: string | null;
  updated_on: string | null;
}

interface TaggerSpec {
  lookback_days?: number;
  tag_ids?: number[];
  function?: string;
  condition?: string;
  conditions?: { field: string; operator: string; values: number[] }[];
  comment?: string;
}

export interface TaggerRow {
  id: number;
  name: string;
  is_enabled: boolean;
  marketplace: string;
  entity_type: TaggerEntityType;
  tagging_level: string;
  created_on: string | null;
  updated_on: string | null;
  deleted_on: string | null;
  created_by: string | null;
  updated_by: string | null;
  deleted_by: string | null;
  program: TaggerProgram;
  spec: TaggerSpec;
}

function parseTaggersResponse(data: Record<string, unknown>): TaggerRow[] {
  const rows: TaggerRow[] = [];
  for (const [key, value] of Object.entries(data)) {
    if (key === "count") continue;
    const taggerId = Number(key);
    if (!Number.isNaN(taggerId) && typeof value === "object" && value !== null) {
      const row = value as Omit<TaggerRow, "entity_type"> & { entity_type?: TaggerEntityType };
      rows.push({ ...row, id: taggerId, entity_type: row.entity_type ?? "advertisement" });
    }
  }
  return rows;
}

export const taggersQueryOptions = () =>
  queryOptions({
    queryKey: ["taggers"],
    queryFn: async () => {
      const data = await apiClient.getTaggers();
      return parseTaggersResponse(data);
    },
  });

export function useCreateTagger() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);

  return useMutation({
    mutationFn: async (input: {
      name: string;
      marketplace: string;
      entity_type: TaggerEntityType;
      start_date: string;
      end_date: string | null;
      spec: {
        lookback_days: number;
        function: "ADD_TAG";
        tag_ids: number[];
        condition: "any" | "all" | "everything";
        conditions: { field: string; operator: string; values: number[] }[];
        comment?: string;
      };
    }) => {
      return await apiClient.createTagger({
        name: input.name,
        marketplace: input.marketplace,
        entity_type: input.entity_type,
        start_date: input.start_date,
        end_date: input.end_date,
        spec: input.spec,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("taggers"),
      });
      successToast({ message: t(($) => $.advertising["Successfully created tagging program"]) });
    },
    onError: () => {
      errorToast({ message: t(($) => $.advertising["Failed to create tagging program"]) });
    },
  });
}

export function useUpdateTagger() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);

  return useMutation({
    mutationFn: async (input: {
      tagger_id: number;
      name?: string;
      is_enabled: boolean;
      entity_type: TaggerEntityType;
      spec: Record<string, unknown>;
      start_date?: string;
      end_date?: string | null;
    }) => {
      return await apiClient.updateTagger({
        tagger_id: input.tagger_id,
        name: input.name,
        is_enabled: input.is_enabled,
        entity_type: input.entity_type,
        spec: input.spec,
        start_date: input.start_date,
        end_date: input.end_date,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("taggers"),
      });
      successToast({ message: t(($) => $.advertising["Successfully updated tagging program"]) });
    },
    onError: () => {
      errorToast({ message: t(($) => $.advertising["Failed to update tagging program"]) });
    },
  });
}

export function useDeleteTagger() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);

  return useMutation({
    mutationFn: async (taggerId: number) => {
      return await apiClient.deleteTagger({ tagger_id: taggerId });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("taggers"),
      });
      successToast({ message: t(($) => $.advertising["Deleted tagging program"]) });
    },
    onError: () => {
      errorToast({ message: t(($) => $.advertising["Failed to delete tagging program"]) });
    },
  });
}
